import { type JSX } from 'react';
import { Link } from 'react-router-dom';
import { KINGDOMS } from '@yuan-math/content';
import { worldState, type Kingdom, type KingdomState, type KingdomStatus, type MissingRequirement } from '@yuan-math/world';
import { registry, useLearner } from '../state/LearnerContext.tsx';

/**
 * The world map — every kingdom on one winding road (docs/rpg.md §33), drawn
 * in `PathMap`'s idiom: a vertical walk whose nodes wander left and right on a
 * fixed cycle in CSS, so it behaves the same at every width.
 *
 * **A seal withholds the island, never the mathematics** (`packages/world/src/types.ts`).
 * A sealed node is not a link to its kingdom: that island is not the student's
 * yet, and neither is any island past it — `openDownwards` leaves `sealed`
 * upward closed, so the seals here are always the tail of the road. What a
 * sealed node shows instead is the mathematics its seal is waiting for, named
 * ("Still to learn: Common Denominators…") rather than gamified, because "learn
 * these three things" is a plan and "locked" is a wall — and every one of those
 * names is a link to the concept, which is the lesson, the practice and the
 * whole chain behind it. Nothing sealed here withholds a lesson; the concepts a
 * seal is waiting for are the shortest route to it.
 *
 * Status comes from `worldState` alone, which reads `knownSet` — never from
 * XP, level or gold. Every status is a colour *and* an icon *and* a word.
 *
 * `arrivedKingdomId` flags the kingdom a student has just been sent here to
 * look at — the one a boss they beat a moment ago has changed the status of.
 * It is a *pointer*, never a claim: the chip beside it still reports whatever
 * `worldState` says, so a highlight can never announce an unlock that did not
 * actually happen.
 */

const STATUS: Record<KingdomStatus, { icon: string; word: string }> = {
  sealed: { icon: '🔒', word: 'Sealed' },
  open: { icon: '🚩', word: 'Open' },
  cleared: { icon: '👑', word: 'Cleared' },
};

/** Which kingdom a concept lives in, so a seal can say whose skills it wants. */
const KINGDOM_HOLDING_CONCEPT = new Map<string, string>();
for (const kingdom of KINGDOMS) {
  for (const region of kingdom.regions) {
    for (const conceptId of region.conceptIds) KINGDOM_HOLDING_CONCEPT.set(conceptId, kingdom.name);
  }
}

const KINGDOM_OF_BOSS = new Map(KINGDOMS.filter((k) => k.bossId).map((k) => [k.bossId as string, k.name]));

/**
 * The status as an icon and a word on a coloured chip — all three carriers.
 *
 * The icon is decorative because the word beside it says the same thing, and
 * the word alone is the ambiguous half of what a screen reader reads out: "Open"
 * arriving after a kingdom's name could be a status or an instruction. The
 * hidden "Status:" in front of it costs a sighted reader nothing and makes the
 * announced version a sentence — which matters most on the map, where ten of
 * these are read one after another.
 */
export function KingdomStatusChip({ status }: { status: KingdomStatus }): JSX.Element {
  const { icon, word } = STATUS[status];
  return (
    <span className={`world-status world-status--${status}`}>
      <span className="visually-hidden">Status: </span>
      <span aria-hidden="true">{icon}</span>
      <span>{word}</span>
    </span>
  );
}

/**
 * One unmet clause, said as mathematics.
 *
 * "Needs 80% of Starter Village skills — you know 36%", then the first few
 * missing concepts by name. The percentages are of the clause's whole concept
 * list, which is what the requirement is stated against.
 *
 * Exported because the kingdom page states the same seal in the same words. A
 * map that said "80% of Starter Village skills" and a kingdom page that said
 * "nearly there" would be two claims about one number.
 */
export function sealClause(missing: MissingRequirement): { headline: string; detail?: string } {
  if (missing.kind === 'boss') {
    const home = KINGDOM_OF_BOSS.get(missing.bossId);
    return { headline: home ? `Needs the ${home} boss defeated.` : 'Needs an earlier boss defeated.' };
  }

  const total = missing.have + missing.conceptIds.length;
  const sources = [
    ...new Set(missing.conceptIds.map((id) => KINGDOM_HOLDING_CONCEPT.get(id) ?? 'earlier kingdoms')),
  ];
  const needPct = Math.round((missing.need / total) * 100);
  const havePct = Math.round((missing.have / total) * 100);

  const titles = missing.conceptIds.map((id) => registry.concept(id)?.title ?? id);
  const shown = titles.slice(0, 3);
  const more = titles.length - shown.length;

  return {
    headline: `Needs ${needPct}% of ${sources.join(' and ')} skills — you know ${havePct}%.`,
    detail: `Still to learn: ${shown.join(', ')}${more > 0 ? ` and ${more} more` : ''}.`,
  };
}

/** The readiness ring around a sealed node: how close its seal is to breaking. */
function ReadinessRing({ value }: { value: number }): JSX.Element {
  const radius = 43;
  const circumference = 2 * Math.PI * radius;
  const fraction = Math.min(1, Math.max(0, value));
  return (
    <svg className="world-node__ring" viewBox="0 0 90 90" aria-hidden="true" focusable="false">
      <circle className="world-node__ring-track" cx="45" cy="45" r={radius} />
      <circle
        className="world-node__ring-fill"
        cx="45"
        cy="45"
        r={radius}
        strokeDasharray={`${circumference * fraction} ${circumference}`}
      />
    </svg>
  );
}

function KingdomStep({
  kingdom,
  state,
  isCurrent,
  arrived,
}: {
  kingdom: Kingdom;
  state: KingdomState;
  isCurrent: boolean;
  /** Arrived here from something that just changed this kingdom's status. */
  arrived: boolean;
}): JSX.Element {
  const { status, readiness } = state;
  const known = state.regions.reduce((sum, region) => sum + region.progress.known, 0);
  const total = state.regions.reduce((sum, region) => sum + region.progress.total, 0);

  const label =
    status === 'sealed'
      ? `${kingdom.name} — Sealed, ${Math.round(readiness * 100)}% ready. Learn the skills below to open it.`
      : `${kingdom.name} — ${STATUS[status].word}, ${known} of ${total} skills known`;

  /*
   * The concepts the seal is waiting for, as doors rather than as a list. Three
   * at most and never the whole clause: this is the next thing to go and do,
   * and a wall of forty links is a list of reasons the kingdom is shut.
   */
  const wanted = state.missing
    .flatMap((missing) => (missing.kind === 'known' ? missing.conceptIds : []))
    .slice(0, 3)
    .map((id) => ({ id, title: registry.concept(id)?.title ?? id }));

  return (
    <li
      className={`world-step world-step--${status} ${isCurrent ? 'world-step--current' : ''} ${arrived ? 'world-step--arrived' : ''}`}
    >
      {/* One flag at a time, and the new thing wins: a student who has just
          beaten a boss is here to see what it changed, and the status word
          under the node is what says *what* changed. */}
      {arrived ? (
        <span className="path-flag path-flag--arrived">Just changed</span>
      ) : isCurrent ? (
        <span className="path-flag">You are here</span>
      ) : null}

      {status === 'sealed' ? (
        <span className={`world-node world-node--${status}`} role="img" aria-label={label}>
          <ReadinessRing value={readiness} />
          <span aria-hidden="true">{kingdom.emoji}</span>
          <span className={`world-node__badge world-node__badge--${status}`} aria-hidden="true">
            {STATUS[status].icon}
          </span>
        </span>
      ) : (
        <Link to={`/adventure/kingdoms/${kingdom.id}`} className={`world-node world-node--${status}`} aria-label={label}>
          <span aria-hidden="true">{kingdom.emoji}</span>
          {status !== 'open' ? (
            <span className={`world-node__badge world-node__badge--${status}`} aria-hidden="true">
              {STATUS[status].icon}
            </span>
          ) : null}
        </Link>
      )}

      <span className="world-step__name">{kingdom.name}</span>
      <KingdomStatusChip status={status} />

      {status === 'sealed' ? (
        <>
          <ul className="world-seal">
            {state.missing.map((missing, index) => {
              const clause = sealClause(missing);
              return (
                <li key={index}>
                  {clause.headline}
                  {clause.detail ? <span className="world-seal__detail"> {clause.detail}</span> : null}
                </li>
              );
            })}
          </ul>
          {/*
            The way in, and it is mathematics rather than a door: each concept
            page holds that concept's lesson, its practice and the chain it
            hangs off. The island is shut; not one of these is.
          */}
          {wanted.length > 0 ? (
            <p className="world-step__learn">
              <span className="world-step__learn-label">Learn now:</span>{' '}
              {wanted.map((concept, index) => (
                <span key={concept.id}>
                  {index > 0 ? ', ' : ''}
                  <Link to={`/concepts/${concept.id}`}>{concept.title}</Link>
                </span>
              ))}
            </p>
          ) : null}
        </>
      ) : (
        <span className="world-step__meta">
          {kingdom.regions.length} regions · {known}/{total} skills known
        </span>
      )}
    </li>
  );
}

export function WorldMap({ arrivedKingdomId }: { arrivedKingdomId?: string } = {}): JSX.Element {
  const { profile } = useLearner();

  // Derived fresh on every render, like mastery everywhere else: readiness
  // genuinely moves as retention decays, so a memo on the profile would lie.
  const states = worldState(
    KINGDOMS,
    registry.graph,
    profile?.concepts ?? {},
    profile?.rpg?.clearedBossIds ?? [],
    Date.now(),
  );
  const byId = new Map(KINGDOMS.map((kingdom) => [kingdom.id, kingdom]));

  // "You are here": the lowest-order open kingdom with unfinished regions —
  // the place the story is happening. A student may of course walk anywhere.
  const currentId =
    states.find(
      (state) =>
        state.status === 'open' && state.regions.some((region) => region.progress.known < region.progress.total),
    )?.kingdomId ?? states.find((state) => state.status === 'open')?.kingdomId;

  return (
    <ol className="world-path">
      {states.map((state) => {
        const kingdom = byId.get(state.kingdomId);
        if (!kingdom) return null;
        return (
          <KingdomStep
            key={kingdom.id}
            kingdom={kingdom}
            state={state}
            isCurrent={state.kingdomId === currentId}
            arrived={state.kingdomId === arrivedKingdomId}
          />
        );
      })}
    </ol>
  );
}
